import { logger } from '@shared_ts/utils/system/logger'

/**
 * Configuration for email signup form behavior
 * @interface EmailSignupConfig
 */
interface EmailSignupConfig {
  /** Unique identifier for this signup instance */
  formId: string
  /** API endpoint to submit to */
  endpoint: string
  /** Tags to apply to the subscriber */
  tags?: string[]
  /** List ID for segmentation */
  listId?: string
  /** Source identifier for analytics */
  source?: string
  /** Custom success message */
  successMessage?: string
  /** Custom error message */
  errorMessage?: string
  /** Whether to disable form after success */
  disableAfterSuccess?: boolean
  /** Custom validation function */
  customValidator?: (email: string) => { valid: boolean; message?: string }
  /** Callback functions */
  onSuccess?: (response: any) => void
  onError?: (error: any) => void
  onSubmit?: (email: string) => void
}

/**
 * Response format from email signup endpoints
 * @interface EmailSignupResponse
 */
interface EmailSignupResponse {
  /** Whether the signup was successful */
  success: boolean
  /** Optional message from the server */
  message?: string
  /** Validation errors keyed by field name */
  errors?: Record<string, string[]>
}

/**
 * Unified email signup service that handles multiple forms
 * with configuration-driven behavior.
 *
 * This service provides a singleton pattern for managing email signups
 * across different forms and widgets, with built-in duplicate submission
 * prevention, validation, and configurable callbacks.
 *
 * @class EmailSignupService
 * @example
 * ```typescript
 * const service = EmailSignupService.getInstance();
 * const config = EmailSignupService.createNewsletterConfig('newsletter-form', 'homepage');
 *
 * const result = await service.submitSignup('user@example.com', config);
 * if (result.success) {
 *   console.log('Signup successful!');
 * }
 * ```
 */
export class EmailSignupService {
  /** Singleton instance of the service */
  private static instance: EmailSignupService
  /**
   * Tracks active submissions to prevent duplicates.
   * Key format: `${formId}-${email}`
   */
  private activeSubmissions = new Set<string>()

  /**
   * Get singleton instance of the email signup service.
   *
   * @static
   * @returns {EmailSignupService} The singleton service instance
   * @example
   * ```typescript
   * const service = EmailSignupService.getInstance();
   * ```
   */
  public static getInstance(): EmailSignupService {
    if (!EmailSignupService.instance) {
      EmailSignupService.instance = new EmailSignupService()
    }
    return EmailSignupService.instance
  }

  /**
   * Submit an email signup using the provided configuration.
   *
   * This method handles the complete signup flow including validation,
   * duplicate prevention, API submission, and callback execution.
   *
   * @async
   * @param {string} email - The email address to subscribe
   * @param {EmailSignupConfig} config - Configuration object for this signup
   * @returns {Promise<EmailSignupResponse>} The API response with success status and message
   * @throws {Error} Network errors are caught and converted to error responses
   *
   * @example
   * ```typescript
   * const config = {
   *   formId: 'newsletter-signup',
   *   endpoint: '/api/newsletter-signup/',
   *   tags: ['WebsiteNewsletter'],
   *   source: 'homepage'
   * };
   *
   * const result = await service.submitSignup('user@example.com', config);
   * console.log(result.success ? 'Success!' : result.message);
   * ```
   */
  public async submitSignup(email: string, config: EmailSignupConfig): Promise<EmailSignupResponse> {
    const submissionKey = `${config.formId}-${email}`

    // Prevent duplicate submissions
    if (this.activeSubmissions.has(submissionKey)) {
      logger('info', `EmailSignupService: Duplicate submission prevented. formId=${config.formId}, email=${email}`)
      return { success: false, message: 'Submission already in progress' }
    }

    // Validate email
    const emailValidation = this.validateEmail(email, config.customValidator)
    if (!emailValidation.valid) {
      return { success: false, message: emailValidation.message }
    }

    // Track submission start
    this.activeSubmissions.add(submissionKey)
    config.onSubmit?.(email)

    try {
      logger(
        'info',
        `EmailSignupService: Starting signup submission. formId=${config.formId}, endpoint=${config.endpoint}, source=${config.source}`
      )

      const formData = this.buildFormData(email, config)
      const response = await this.makeRequest(config.endpoint, formData)

      if (response.success) {
        logger('info', `EmailSignupService: Signup successful. formId=${config.formId}, email=${email}`)
        config.onSuccess?.(response)
        return {
          success: true,
          message: config.successMessage || response.message || 'Thank you for signing up!'
        }
      } else {
        logger('warn', `EmailSignupService: Signup failed. formId=${config.formId}, message=${response.message}`)
        config.onError?.(response)
        return {
          success: false,
          message: config.errorMessage || response.message || 'Something went wrong. Please try again.',
          errors: response.errors
        }
      }
    } catch (error) {
      logger('error', `EmailSignupService: Network error. formId=${config.formId}, error=${error}`)
      config.onError?.(error)
      return {
        success: false,
        message: 'Network error. Please try again later.'
      }
    } finally {
      // Clean up tracking
      this.activeSubmissions.delete(submissionKey)
    }
  }

  /**
   * Validate an email address using basic regex and optional custom validation.
   *
   * First applies a basic email format check, then delegates to custom
   * validator if provided. The custom validator is a function
   * that you pass directly to the configuration.
   *
   * @private
   * @param {string} email - The email address to validate
   * @param {function} [customValidator] - Optional custom validation function that takes an email string and returns validation result
   * @param {string} customValidator.email - Email to validate
   * @param {Object} customValidator.returns - Validation result
   * @param {boolean} customValidator.returns.valid - Whether email is valid
   * @param {string} [customValidator.returns.message] - Optional error message
   * @returns {Object} Validation result
   * @returns {boolean} returns.valid - Whether the email passed validation
   * @returns {string} [returns.message] - Error message if validation failed
   *
   * @example
   * ```typescript
   * // Basic usage without custom validator
   * const result = this.validateEmail('user@example.com');
   * if (!result.valid) {
   *   console.error(result.message);
   * }
   *
   * // Usage with custom validator function
   * const customValidator = (email: string) => {
   *   if (email.endsWith('@company.com')) {
   *     return { valid: true };
   *   }
   *   return { valid: false, message: 'Only company emails are allowed' };
   * };
   *
   * const result = this.validateEmail('user@company.com', customValidator);
   * ```
   */
  private validateEmail(
    email: string,
    customValidator?: (email: string) => {
      valid: boolean
      message?: string
    }
  ): { valid: boolean; message?: string } {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return {
        valid: false,
        message: 'Please enter a valid email address.'
      }
    }

    // Apply custom validation if provided
    if (customValidator) {
      return customValidator(email)
    }

    return { valid: true }
  }

  /**
   * Build FormData payload for API submission.
   *
   * Creates a FormData object with the email and all configured
   * optional fields (tags, listId, source, formId).
   *
   * @private
   * @param {string} email - The email address to include
   * @param {EmailSignupConfig} config - Configuration containing optional fields
   * @returns {FormData} Populated FormData object ready for submission
   *
   * @example
   * ```typescript
   * const formData = this.buildFormData('user@example.com', {
   *   formId: 'newsletter',
   *   tags: ['WebsiteNewsletter'],
   *   source: 'homepage'
   * });
   * ```
   */
  private buildFormData(email: string, config: EmailSignupConfig): FormData {
    const formData = new FormData()
    formData.append('email', email)

    // Add optional fields based on configuration
    if (config.tags && config.tags.length > 0) {
      formData.append('tags', JSON.stringify(config.tags))
    }

    if (config.listId) {
      formData.append('list_id', config.listId)
    }

    if (config.source) {
      formData.append('source', config.source)
    }

    // Add form ID for backend tracking
    formData.append('form_id', config.formId)

    return formData
  }

  /**
   * Make an HTTP POST request to the signup endpoint.
   *
   * Includes CSRF token from cookies or hidden form input for Django compatibility.
   * Handles network errors and non-2xx responses.
   *
   * @private
   * @async
   * @param {string} endpoint - The API endpoint URL to submit to
   * @param {FormData} formData - The form data to submit
   * @returns {Promise<EmailSignupResponse>} Parsed JSON response from the server
   * @throws {Error} When the HTTP response is not ok (non-2xx status codes)
   *
   * @example
   * ```typescript
   * try {
   *   const response = await this.makeRequest('/api/signup/', formData);
   *   console.log(response.success);
   * } catch (error) {
   *   console.error('Request failed:', error.message);
   * }
   * ```
   */
  private async makeRequest(endpoint: string, formData: FormData): Promise<EmailSignupResponse> {
    // Get CSRF token from the page
    const csrfToken = this.getCsrfToken()

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'X-CSRFToken': csrfToken
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    return await response.json()
  }

  /**
   * Get CSRF token from cookies or hidden input on the page.
   *
   * First attempts to find the token in document cookies, then falls back
   * to looking for a hidden input field with name "csrfmiddlewaretoken".
   * This provides compatibility with Django's CSRF protection.
   *
   * @private
   * @returns {string} The CSRF token if found, empty string otherwise
   *
   * @example
   * ```typescript
   * const token = this.getCsrfToken();
   * if (token) {
   *   // Token found, can make authenticated request
   * }
   * ```
   */
  private getCsrfToken(): string {
    const name = 'csrftoken'
    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      cookie = cookie.trim()
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }

    const csrfInput = document.querySelector('input[name="csrfmiddlewaretoken"]') as HTMLInputElement
    return csrfInput?.value || ''
  }

  /**
   * Create a default configuration for newsletter footer signup.
   *
   * Provides a pre-configured setup for the main newsletter with sensible
   * defaults for endpoint, success handling, and error messages.
   *
   * Tags are intentionally omitted — the backend resolves tenant-specific
   * tags from DRAWER_WIDGET_TENANT_CONFIG / NEWSLETTER_WIDGET_TENANT_CONFIG.
   *
   * @static
   * @param {string} formId - Unique identifier for the form instance
   * @param {string} source - Source identifier for analytics tracking
   * @returns {EmailSignupConfig} Pre-configured signup configuration
   *
   * @example
   * ```typescript
   * const config = EmailSignupService.createNewsletterConfig(
   *   'homepage-newsletter',
   *   'homepage'
   * );
   * const service = EmailSignupService.getInstance();
   * await service.submitSignup('user@example.com', config);
   * ```
   */
  public static createNewsletterConfig(formId: string, source: string): EmailSignupConfig {
    return {
      formId,
      endpoint: '/api-root/newsletter-signup/',
      source,
      disableAfterSuccess: true,
      successMessage: 'Thank you for signing up!',
      errorMessage: 'Unable to process your subscription. Please try again.'
    }
  }
}
